import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { forwardRef } from 'react'

import { Spinner } from './Spinner'

import { cn } from '@/lib/utils/cn'


export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'link'
export type ButtonSize = 'sm' | 'md' | 'lg'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
  icon?: ReactNode
  iconPosition?: 'left' | 'right'
  fullWidth?: boolean
}

const VARIANTS: Record<ButtonVariant, string> = {
  // One filled action per view. Competing CTAs are an anti-pattern here.
  primary: 'bg-action text-text-on-action hover:bg-action-hover',
  secondary:
    'bg-surface text-text border border-border-strong hover:bg-surface-hover',
  ghost: 'bg-transparent text-text-secondary hover:bg-surface-hover hover:text-text',
  // Destructive only, and only after confirmation.
  danger: 'bg-danger text-white hover:opacity-90',
  link: 'bg-transparent text-action hover:underline p-0 h-auto',
}

// Visual height. Padding brings every size to a >= 44px hit area.
const SIZES: Record<ButtonSize, string> = {
  sm: 'min-h-[44px] px-3 text-caption',
  md: 'min-h-[44px] px-4 text-compact',
  lg: 'h-12 px-6 text-body',
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    loading = false,
    icon,
    iconPosition = 'left',
    fullWidth = false,
    disabled,
    className,
    children,
    ...props
  },
  ref,
) {
  const isDisabled = disabled || loading

  return (
    <button
      ref={ref}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      className={cn(
        'pressable inline-flex items-center justify-center gap-2',
        'rounded-control font-semibold whitespace-nowrap',
        'transition-colors duration-control ease-standard',
        'disabled:cursor-not-allowed disabled:opacity-50',
        VARIANTS[variant],
        variant !== 'link' && SIZES[size],
        fullWidth && 'w-full',
        className,
      )}
      {...props}
    >
      {/* The label stays put while loading, so the button does not change width. */}
      {loading ? (
        <Spinner size={size === 'lg' ? 18 : 14} />
      ) : (
        icon && iconPosition === 'left' && <span aria-hidden="true">{icon}</span>
      )}
      {children}
      {!loading && icon && iconPosition === 'right' && (
        <span aria-hidden="true">{icon}</span>
      )}
    </button>
  )
})

/**
 * Icon-only button.
 *
 * `label` is required, not optional: an icon button without an accessible name
 * is invisible to a screen reader, and making it optional guarantees someone
 * forgets it.
 */
export const IconButton = forwardRef<
  HTMLButtonElement,
  Omit<ButtonProps, 'children' | 'icon'> & { icon: ReactNode; label: string }
>(function IconButton({ icon, label, size = 'md', className, ...props }, ref) {
  const box = size === 'sm' ? 'size-[44px]' : size === 'lg' ? 'size-12' : 'size-[44px]'
  return (
    <Button
      ref={ref}
      aria-label={label}
      title={label}
      size={size}
      className={cn(box, 'px-0', className)}
      {...props}
    >
      <span aria-hidden="true">{icon}</span>
    </Button>
  )
})
